import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
const CATEGORIES = [{
  id: 'all',
  label: 'All Tools'
}, {
  id: 'coding',
  label: 'Coding'
}, {
  id: 'data',
  label: 'Data Analysis'
}, {
  id: 'documents',
  label: 'Documents'
}, {
  id: 'image',
  label: 'Image/Video'
}, {
  id: 'research',
  label: 'Research'
}, {
  id: 'productivity',
  label: 'Productivity'
}];
const TOOLS = [
// Coding
{
  id: 'claude-code',
  name: 'Claude Code',
  category: 'coding',
  rating: 4.9,
  desc: 'Agentic coding environment that edits files, runs tests and iterates on your code.',
  tags: ['agentic', 'claude', 'coding', 'environment', 'files', 'tests'],
  pricing: 'Paid',
  url: 'https://claude.ai'
}, {
  id: 'cowork',
  name: 'Cowork',
  category: 'coding',
  rating: 4.8,
  desc: 'Desktop mode that automates complex multi-step work and produces polished outputs.',
  tags: ['automates', 'claude', 'coding', 'desktop', 'multi-step', 'outputs', 'polished', 'produces', 'work'],
  pricing: 'Paid',
  url: 'https://claude.ai'
}, {
  id: 'code-interpreter',
  name: 'Code Interpreter',
  category: 'coding',
  rating: 4.7,
  desc: 'Secure Python environment for calculations, data analysis and debugging.',
  tags: ['analysis', 'calculations', 'chatgpt', 'debugging', 'python', 'secure'],
  pricing: 'Paid',
  url: 'https://chatgpt.com'
}, {
  id: 'custom-gpts-coding',
  name: 'Custom GPTs for Coding',
  category: 'coding',
  rating: 4.6,
  desc: 'Create specialized coding assistants by defining instructions and connecting APIs.',
  tags: ['apis', 'assistants', 'chatgpt', 'coding', 'connecting', 'create', 'defining', 'instructions'],
  pricing: 'Paid',
  url: 'https://chatgpt.com/gpts'
}, {
  id: 'gemini-code-assist',
  name: 'Gemini Code Assist & CLI',
  category: 'coding',
  rating: 4.5,
  desc: 'Code completion, analysis and command-line tools for writing and debugging code.',
  tags: ['analysis', 'assist', 'cli', 'code', 'completion', 'debugging', 'gemini'],
  pricing: 'Freemium',
  url: 'https://gemini.google.com'
}, {
  id: 'jules',
  name: 'Jules',
  category: 'coding',
  rating: 4.7,
  desc: 'Asynchronous coding agent included with AI Pro for daily coding assistance.',
  tags: ['agent', 'assistance', 'asynchronous', 'coding', 'daily', 'pro'],
  pricing: 'Paid',
  url: 'https://claude.ai'
},
// Data Analysis
{
  id: 'claude-code-data',
  name: 'Claude Code (Data Tasks)',
  category: 'data',
  rating: 4.8,
  desc: 'Use Claude Code to process datasets, run statistical tests and build visualizations.',
  tags: ['build', 'claude', 'data analysis', 'datasets', 'process', 'statistical', 'visualizations'],
  pricing: 'Paid',
  url: 'https://claude.ai'
}, {
  id: 'code-interpreter-data',
  name: 'Code Interpreter (Data)',
  category: 'data',
  rating: 4.7,
  desc: 'Upload CSVs and perform complex calculations and visualizations in Python.',
  tags: ['calculations', 'chatgpt', 'complex', 'csvs', 'python', 'upload', 'visualizations'],
  pricing: 'Paid',
  url: 'https://chatgpt.com'
}, {
  id: 'spreadsheet-analysis',
  name: 'Spreadsheet & Code Analysis',
  category: 'data',
  rating: 4.5,
  desc: 'Analyze spreadsheets and code within the Gemini Pro tier.',
  tags: ['analysis', 'analyze', 'data analysis', 'gemini', 'spreadsheets'],
  pricing: 'Paid',
  url: 'https://gemini.google.com'
}, {
  id: 'notebooklm',
  name: 'NotebookLM',
  category: 'data',
  rating: 4.9,
  desc: 'Upload documents and videos to ask questions, receive citations and audio overviews.',
  tags: ['ask', 'audio', 'citations', 'documents', 'gemini', 'overviews', 'questions', 'upload', 'videos'],
  pricing: 'Free',
  url: 'https://notebooklm.google.com'
},
// Documents
{
  id: 'artifacts-projects',
  name: 'Artifacts & Projects',
  category: 'documents',
  rating: 4.8,
  desc: 'Create persistent interactive documents and manage long-term projects with consistency.',
  tags: ['artifacts', 'claude', 'consistency', 'create', 'documents', 'interactive', 'manage', 'projects'],
  pricing: 'Paid',
  url: 'https://claude.ai'
}, {
  id: 'cowork-documents',
  name: 'Cowork (Documents)',
  category: 'documents',
  rating: 4.7,
  desc: 'Automate formatting, organize files and generate spreadsheets or slides.',
  tags: ['automate', 'claude', 'cowork', 'documents', 'files', 'formatting', 'generate'],
  pricing: 'Paid',
  url: 'https://claude.ai'
}, {
  id: 'file-upload',
  name: 'File Upload & Summarization',
  category: 'documents',
  rating: 4.6,
  desc: 'Upload PDFs and ask ChatGPT to summarize or extract key findings.',
  tags: ['ask', 'chatgpt', 'documents', 'extract', 'findings', 'pdfs', 'summarize', 'upload'],
  pricing: 'Paid',
  url: 'https://chatgpt.com'
}, {
  id: 'canvas',
  name: 'Canvas',
  category: 'documents',
  rating: 4.8,
  desc: 'Integrated workspace for outlining, drafting and editing long-form content.',
  tags: ['canvas', 'chatgpt', 'content', 'drafting', 'editing', 'integrated', 'outlining', 'workspace'],
  pricing: 'Paid',
  url: 'https://chatgpt.com'
}, {
  id: 'gemini-workspace',
  name: 'Gemini in Workspace',
  category: 'documents',
  rating: 4.7,
  desc: 'Side-panel assistance for writing emails, summarizing documents and generating slides.',
  tags: ['assistance', 'documents', 'emails', 'gemini', 'generating', 'slides', 'summarizing', 'writing'],
  pricing: 'Paid',
  url: 'https://workspace.google.com'
}, {
  id: 'notebooklm-research',
  name: 'NotebookLM (Research)',
  category: 'documents',
  rating: 4.9,
  desc: 'Research and summarization tool with study guides, mind maps and citations.',
  tags: ['citations', 'documents', 'gemini', 'maps', 'mind', 'research', 'summarization'],
  pricing: 'Free',
  url: 'https://notebooklm.google.com'
},
// Image/Video
{
  id: 'artifacts-charts',
  name: 'Artifacts (Charts)',
  category: 'image',
  rating: 4.7,
  desc: "Create interactive charts and simple applications using Claude's Artifacts feature.",
  tags: ['applications', 'artifacts', 'charts', 'claude', 'create', 'interactive', 'simple'],
  pricing: 'Paid',
  url: 'https://claude.ai'
}, {
  id: 'chatgpt-images',
  name: 'ChatGPT Images (DALL-E)',
  category: 'image',
  rating: 4.8,
  desc: 'Generate and edit images directly within ChatGPT; images are saved for reuse.',
  tags: ['are', 'chatgpt', 'dall', 'directly', 'edit', 'generate', 'images', 'saved'],
  pricing: 'Paid',
  url: 'https://chatgpt.com'
}, {
  id: 'flow',
  name: 'Flow',
  category: 'image',
  rating: 4.6,
  desc: 'Create videos from text, frames or ingredients; includes scene builder.',
  tags: ['builder', 'create', 'flow', 'frames', 'ingredients', 'scene', 'text', 'videos'],
  pricing: 'Paid',
  url: 'https://www.flow.ai'
}, {
  id: 'nano-banana',
  name: 'Nano Banana',
  category: 'image',
  rating: 4.5,
  desc: 'AI image editing: remove objects, apply styles or transform photos.',
  tags: ['apply', 'banana', 'editing', 'image', 'nano', 'objects', 'photos', 'remove', 'styles'],
  pricing: 'Freemium',
  url: 'https://nanobanana.ai'
}, {
  id: 'imagen',
  name: 'Imagen 3/4',
  category: 'image',
  rating: 4.9,
  desc: 'Photorealistic text-to-image generation accessible via AI Studio or Vertex AI.',
  tags: ['34', 'accessible', 'ai', 'generation', 'gemini', 'imagen', 'photorealistic', 'studio', 'vertex'],
  pricing: 'Paid',
  url: 'https://deepmind.google/technologies/imagen-3/'
},
// Research
{
  id: 'research-extended',
  name: 'Research & Extended Thinking',
  category: 'research',
  rating: 4.9,
  desc: 'Deep research across many sources with transparent reasoning modes.',
  tags: ['across', 'claude', 'deep', 'modes', 'reasoning', 'research', 'sources', 'transparent'],
  pricing: 'Paid',
  url: 'https://claude.ai'
}, {
  id: 'cowork-research',
  name: 'Cowork Research Tasks',
  category: 'research',
  rating: 4.7,
  desc: 'Synthesise research, extract themes and generate reports across your files.',
  tags: ['across', 'claude', 'cowork', 'extract', 'files', 'generate', 'research', 'themes'],
  pricing: 'Paid',
  url: 'https://claude.ai'
}, {
  id: 'deep-research-chatgpt',
  name: 'Deep Research (ChatGPT)',
  category: 'research',
  rating: 4.8,
  desc: 'Use Code Interpreter and browsing to conduct deep research with citations.',
  tags: ['browsing', 'chatgpt', 'citations', 'conduct', 'deep'],
  pricing: 'Paid',
  url: 'https://chatgpt.com'
}, {
  id: 'deep-research-gemini',
  name: 'Deep Research (Gemini)',
  category: 'research',
  rating: 4.7,
  desc: 'Autonomous research synthesis with citations and personalized context.',
  tags: ['autonomous', 'citations', 'context', 'gemini', 'research'],
  pricing: 'Paid',
  url: 'https://gemini.google.com'
}, {
  id: 'notebooklm-research-2',
  name: 'NotebookLM (Research)',
  category: 'research',
  rating: 4.9,
  desc: 'Upload sources and ask questions with citations and audio overviews.',
  tags: ['ask', 'audio', 'citations', 'gemini', 'overviews', 'questions', 'sources'],
  pricing: 'Free',
  url: 'https://notebooklm.google.com'
},
// Productivity
{
  id: 'projects',
  name: 'Projects',
  category: 'productivity',
  rating: 4.8,
  desc: 'Maintain persistent instructions and archives for consistent AI interactions.',
  tags: ['archives', 'claude', 'consistent', 'instructions', 'interactions', 'persistent'],
  pricing: 'Paid',
  url: 'https://claude.ai'
}, {
  id: 'tasks-pulse',
  name: 'Tasks & Pulse',
  category: 'productivity',
  rating: 4.6,
  desc: 'Automate recurring prompts and receive personalized updates based on your usage.',
  tags: ['automate', 'based', 'chatgpt', 'personalized', 'prompts', 'pulse', 'recurring', 'tasks', 'updates'],
  pricing: 'Paid',
  url: 'https://chatgpt.com'
}, {
  id: 'app-directory',
  name: 'App Directory',
  category: 'productivity',
  rating: 4.7,
  desc: 'Browse and add apps that integrate external services directly into chat.',
  tags: ['add', 'browse', 'chat', 'chatgpt', 'directory', 'external', 'integrate', 'services'],
  pricing: 'Paid',
  url: 'https://chatgpt.com'
}, {
  id: 'workspace-ai',
  name: 'Workspace AI',
  category: 'productivity',
  rating: 4.8,
  desc: 'Use AI across Gmail, Docs, Sheets, Slides, Meet and Drive for drafting, summarizing and analyzing.',
  tags: ['across', 'analyzing', 'docs', 'drafting', 'drive', 'gemini', 'gmail', 'meet', 'sheets', 'summarizing'],
  pricing: 'Paid',
  url: 'https://workspace.google.com'
}, {
  id: 'gemini-live',
  name: 'Gemini Live & Voice',
  category: 'productivity',
  rating: 4.7,
  desc: 'Natural voice conversations and real-time camera input for hands-free assistance.',
  tags: ['assistance', 'camera', 'conversations', 'gemini', 'hands-free', 'input', 'live', 'natural', 'real-time', 'voice'],
  pricing: 'Freemium',
  url: 'https://gemini.google.com'
}];
export function ProductivityToolsPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    document.title = 'AI Productivity Tools | ShoppingExpert.ai';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Discover powerful AI tools for coding, data analysis, documents, research and more. Boost your productivity with cutting-edge AI assistants.');
    }
    window.scrollTo(0, 0);
  }, []);
  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return TOOLS.filter(tool => {
      const inCategory = activeCategory === 'all' || tool.category === activeCategory;
      const inQuery = !query || tool.name.toLowerCase().includes(query) || tool.desc.toLowerCase().includes(query) || tool.tags.some(tag => tag.toLowerCase().includes(query));
      return inCategory && inQuery;
    }).sort((a, b) => b.rating - a.rating);
  }, [activeCategory, searchQuery]);
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: TOOLS.length
    };
    TOOLS.forEach(tool => {
      counts[tool.category] = (counts[tool.category] || 0) + 1;
    });
    return counts;
  }, []);
  return <div className="min-h-screen bg-slate-50" data-id="element-1006">
      {/* Header */}
      <header className="sticky top-0 z-10 backdrop-blur supports-[backdrop-filter]:bg-white/70 bg-white/90 border-b border-slate-200" data-id="element-1007">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4" data-id="element-1008">
          <div className="flex items-center justify-between" data-id="element-1009">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-1010">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4yJ3FscyPfrN1xAm7crLNW/ChatGPT_Image_Dec_2%2C_2025%2C_10_42_07_PM.png" alt="Shopping Expert.ai Logo" className="h-12 object-contain" data-id="element-1011" />
              <div data-id="element-1012">
                <span className="text-xl font-bold text-slate-900" data-id="element-1013">
                  AI Shopping Expert
                </span>
                <p className="text-xs text-slate-500" data-id="element-1014">AI Productivity Tools</p>
              </div>
            </Link>
            <nav className="hidden md:flex items-center gap-4 text-sm" data-id="element-1015">
              <Link to="/" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-1016">
                Home
              </Link>
              <Link to="/most-popular-ai-shopping-assistants" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-1017">
                Most Popular
              </Link>
              <Link to="/productivity-tools" className="text-slate-900 font-semibold border-b-2 border-slate-900 pb-1" data-id="element-1018">
                Productivity Tools
              </Link>
              <Link to="/research" className="text-slate-600 hover:text-slate-900 font-medium" data-id="element-1019">
                Research
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-12" data-id="element-1020">
        {/* Hero Section */}
        <div className="text-center mb-12" data-id="element-1021">
          <h1 className="text-4xl font-bold text-slate-900 mb-4" data-id="element-1022">
            AI Productivity Tools
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-8" data-id="element-1023">
            Discover powerful AI tools for coding, data analysis, documents,
            research and more. Boost your productivity with cutting-edge AI
            assistants.
          </p>

          {/* Search */}
          <div className="max-w-2xl mx-auto" data-id="element-1024">
            <input type="text" placeholder="Search tools by name, description, or tags..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm shadow-sm outline-none focus:ring-2 focus:ring-slate-300" data-id="element-1025" />
          </div>
        </div>

        {/* Categories */}
        <div className="mb-8" data-id="element-1026">
          <p className="text-sm font-semibold text-slate-700 mb-3" data-id="element-1027">
            Categories:
          </p>
          <div className="flex flex-wrap gap-2" data-id="element-1028">
            {CATEGORIES.map(cat => <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className={['rounded-full px-4 py-2 text-sm border transition-colors', activeCategory === cat.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'].join(' ')} data-id="element-1029">
                {cat.label} ({categoryCounts[cat.id] || 0})
              </button>)}
          </div>
        </div>

        {/* Tools Grid */}
        <div className="mb-8" data-id="element-1030">
          <p className="text-sm text-slate-600 mb-4" data-id="element-1031">
            Showing {filtered.length} of {TOOLS.length} tools
          </p>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-1032">
            {filtered.map(tool => <div key={tool.id} className="rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6" data-id="element-1033">
                <div className="flex items-start justify-between mb-3" data-id="element-1034">
                  <div data-id="element-1035">
                    <h3 className="font-bold text-lg text-slate-900 mb-1" data-id="element-1036">
                      {tool.name}
                    </h3>
                    <span className="inline-block px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200" data-id="element-1037">
                      {CATEGORIES.find(c => c.id === tool.category)?.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-sm font-semibold text-slate-900" data-id="element-1038">
                    <Star size={16} className="fill-yellow-400 text-yellow-400" data-id="element-1039" />
                    {tool.rating}
                  </div>
                </div>

                <p className="text-sm text-slate-700 mb-4 line-clamp-3" data-id="element-1040">
                  {tool.desc}
                </p>

                <div className="flex flex-wrap gap-1 mb-4" data-id="element-1041">
                  {tool.tags.slice(0, 6).map((tag, idx) => <span key={idx} className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-600" data-id="element-1042">
                      {tag}
                    </span>)}
                </div>

                <div className="flex items-center justify-between" data-id="element-1043">
                  <span className={['text-xs font-semibold px-2 py-1 rounded', tool.pricing === 'Free' ? 'bg-green-100 text-green-700' : tool.pricing === 'Freemium' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'].join(' ')} data-id="element-1044">
                    {tool.pricing}
                  </span>
                  <a href={tool.url} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-slate-900 hover:text-slate-600 transition-colors" data-id="element-1045">
                    Visit →
                  </a>
                </div>
              </div>)}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white mt-16" data-id="element-1046">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8" data-id="element-1047">
          <div className="text-center mb-4" data-id="element-1048">
            <p className="text-sm font-semibold text-slate-900 mb-1" data-id="element-1049">
              ShoppingExpert.ai
            </p>
            <p className="text-xs text-slate-600" data-id="element-1050">
              The independent directory of AI Shopping Agents & Productivity
              Tools.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500" data-id="element-1051">
            <div data-id="element-1052">© 2025 ShoppingExpert.ai • All rights reserved</div>
            <div className="flex items-center gap-3" data-id="element-1053">
              <Link to="/about" className="underline" data-id="element-1054">
                About
              </Link>
              <Link to="/faq" className="underline" data-id="element-1055">
                FAQ
              </Link>
              <Link to="/contact" className="underline" data-id="element-1056">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}